import { RouteComponent } from './route.component'
import { RouteAction, ActiveRoute } from './router.types'

export function compareCustomElementConstructors(
	constructor1: CustomElementConstructor | Function,
	constructor2: CustomElementConstructor | Function,
): boolean {
	if (constructor1 === constructor2) {
		return true
	}

	if (typeof constructor1 !== 'function' || typeof constructor2 !== 'function') {
		return false
	}

	// Name equality survives minification-safe builds where the constructor's
	// identity changes across module reloads but its declared name doesn't.
	if (constructor1.name && constructor2.name && constructor1.name === constructor2.name) {
		return true
	}

	const obs1 = (constructor1 as any).observedAttributes
	const obs2 = (constructor2 as any).observedAttributes

	if (obs1 && obs2) {
		if (Array.isArray(obs1) && Array.isArray(obs2)) {
			if (obs1.length !== obs2.length) return false
			return obs1.every((attr, i) => attr === obs2[i])
		}
	}

	try {
		const proto1 = constructor1.prototype
		const proto2 = constructor2.prototype

		if (Object.getPrototypeOf(proto1) === Object.getPrototypeOf(proto2)) {
			const keys1 = Object.getOwnPropertyNames(proto1).toSorted()
			const keys2 = Object.getOwnPropertyNames(proto2).toSorted()

			return keys1.length === keys2.length && keys1.every((key, i) => key === keys2[i])
		}
	} catch {}

	return false
}

export function normalizeTagName(tagName: string): string {
	return tagName.toLowerCase().replace(/[^a-z0-9]/g, '')
}

export function getTagName(component: RouteComponent): string | null {
	if (typeof component === 'string') {
		return component.toLowerCase()
	}

	if (component instanceof HTMLElement) {
		return component.tagName.toLowerCase()
	}

	if (typeof component === 'function') {
		// Try to get custom element name from constructor
		const name = component.name
		if (name) {
			// Convert PascalCase to kebab-case
			return name
				.replace(/([A-Z])/g, '-$1')
				.toLowerCase()
				.replace(/^-/, '')
		}
	}

	return null
}

export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
	const output = { ...target }

	for (const key in source) {
		if (source.hasOwnProperty(key)) {
			const sourceValue = source[key]
			const targetValue = output[key]

			if (isObject(sourceValue) && isObject(targetValue)) {
				output[key] = deepMerge(targetValue, sourceValue)
			} else {
				output[key] = sourceValue as T[Extract<keyof T, string>]
			}
		}
	}

	return output
}

export function isObject(obj: any): obj is Record<string, any> {
	return (
		obj !== null &&
		typeof obj === 'object' &&
		obj.constructor === Object &&
		Object.prototype.toString.call(obj) === '[object Object]'
	)
}

export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
	let timeout: ReturnType<typeof setTimeout> | null = null

	return function (this: any, ...args: Parameters<T>) {
		// oxlint-disable-next-line typescript/no-this-alias
		const context = this

		if (timeout !== null) {
			clearTimeout(timeout)
		}

		timeout = setTimeout(() => {
			func.apply(context, args)
			timeout = null
		}, wait)
	}
}

export function encodeRouteState(state: Record<string, unknown>): string {
	try {
		const json = JSON.stringify(state)
		return encodeURIComponent(json)
	} catch (error) {
		console.error('Failed to encode route state:', error)
		return ''
	}
}

export function decodeRouteState(encoded: string): Record<string, unknown> {
	if (!encoded) return {}

	try {
		const decoded = decodeURIComponent(encoded)
		const parsed = JSON.parse(decoded)

		if (isObject(parsed)) {
			return parsed
		}
	} catch (error) {
		console.error('Failed to decode route state:', error)
	}

	return {}
}

export function compareRouteActions(a: RouteAction, b: RouteAction): boolean {
	if (a.area !== b.area) return false

	if (typeof a.component !== typeof b.component) return false

	if (typeof a.component === 'string' && typeof b.component === 'string') {
		if (normalizeTagName(a.component) !== normalizeTagName(b.component)) {
			return false
		}
	} else if (typeof a.component === 'function' && typeof b.component === 'function') {
		if (!compareCustomElementConstructors(a.component, b.component)) {
			return false
		}
	} else if (a.component !== b.component) {
		return false
	}

	if (JSON.stringify(a.state || {}) !== JSON.stringify(b.state || {})) {
		return false
	}

	if (JSON.stringify(a.params || {}) !== JSON.stringify(b.params || {})) {
		return false
	}

	return true
}

export function compareActiveRoutes(a: ActiveRoute, b: ActiveRoute): boolean {
	return (
		a.area === b.area &&
		a.component === b.component &&
		JSON.stringify(a.state || {}) === JSON.stringify(b.state || {}) &&
		JSON.stringify(a.params || {}) === JSON.stringify(b.params || {})
	)
}

export function createRouteCacheKey(route: RouteAction): string {
	const tagName = getTagName(route.component) || 'unknown'
	const stateHash = simpleHash(JSON.stringify(route.state || {}))
	const paramsHash = simpleHash(JSON.stringify(route.params || {}))

	return `${route.area}:${tagName}:${stateHash}:${paramsHash}`
}

function simpleHash(str: string): string {
	let hash = 0

	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i)
		hash = (hash << 5) - hash + char
		hash = hash & hash
	}

	return Math.abs(hash).toString(36)
}

export function sanitizeRouteState(
	state: Record<string, unknown>,
	keysToRemove: string[] = ['password', 'token', 'secret', 'apiKey'],
): Record<string, unknown> {
	const sanitized: Record<string, unknown> = {}

	for (const key in state) {
		if (state.hasOwnProperty(key) && !keysToRemove.includes(key)) {
			const value = state[key]

			if (isObject(value)) {
				sanitized[key] = sanitizeRouteState(value, keysToRemove)
			} else if (Array.isArray(value)) {
				sanitized[key] = value.map(item => (isObject(item) ? sanitizeRouteState(item, keysToRemove) : item))
			} else {
				sanitized[key] = value
			}
		}
	}

	return sanitized
}

export function extractQueryParams(url?: string | URLSearchParams): Record<string, string> {
	const params: Record<string, string> = {}

	let searchParams: URLSearchParams

	if (url instanceof URLSearchParams) {
		searchParams = url
	} else if (typeof url === 'string') {
		const urlObj = new URL(url, window.location.origin)
		searchParams = urlObj.searchParams
	} else {
		searchParams = new URLSearchParams(window.location.search)
	}

	searchParams.forEach((value, key) => {
		params[key] = value
	})

	return params
}

// The names a dynamic pattern captures, in segment order. The one reading of
// "what are this pattern's params" — the predicate below, the URL writer in
// area.service.ts, and any caller that needs the key set all derive from here,
// because a second reading of the same pattern is free to disagree with this one
// and nothing compares them.
export function patternParamNames(when: string | undefined): string[] {
	if (typeof when !== 'string') return []
	return when
		.split('/')
		.filter(segment => segment.startsWith(':'))
		.map(segment => segment.slice(1))
}

// A route's `when` is a dynamic path pattern the moment one of its `/`-separated
// segments STARTS with `:` — `/:eventId`, `/events/:eventId` — as opposed to the
// plain exact-match segment name (`admin`) every existing route already declares.
// The two forms share one field because they answer the same question ("what URL
// identifies this route") at different granularity: a bare name matches one
// segment anywhere in the path, a pattern matches the whole path
// position-by-position. A colon anywhere else in a segment (`checkout:step`)
// names no parameter, so it is not a pattern: reading it as one captures nothing
// and still claims the address away from prettyURL and the areas blob, rewriting
// the URL of a consumer who only ever declared an exact route.
export function isDynamicRoutePattern(when: string | undefined): when is string {
	return patternParamNames(when).length > 0
}

// Anchored, whole-path match — every pattern segment must line up with a path
// segment at the same position, so `/events/:eventId` never matches `/events`
// or `/events/2026/keynote`. A literal segment must match verbatim; a `:param`
// segment must be non-empty. Returns null on any mismatch, including arity.
export function matchPathPattern(pattern: string, pathname: string): Record<string, string> | null {
	const patternSegments = pattern.split('/').filter(Boolean)
	const pathSegments = pathname.split('/').filter(Boolean)
	if (patternSegments.length !== pathSegments.length) return null

	const params: Record<string, string> = {}
	for (let i = 0; i < patternSegments.length; i++) {
		const patternSegment = patternSegments[i]
		const pathSegment = pathSegments[i]
		if (patternSegment.startsWith(':')) {
			if (!pathSegment) return null
			// A segment the URL decoder rejects (`/100%`) is a NON-MATCH, not an
			// exception. This runs inside the area's startup and popstate `map`,
			// upstream of every catchError in that pipeline, so a throw here errors
			// the area's whole navigation stream: `.default` never mounts and no
			// later push ever lands again for the page's lifetime. Returning null
			// lets resolution fall through to the next route, then to `.default`.
			let decoded: string
			try {
				decoded = decodeURIComponent(pathSegment)
			} catch {
				return null
			}
			params[patternSegment.slice(1)] = decoded
		} else if (patternSegment !== pathSegment) {
			return null
		}
	}
	return params
}

// A pattern — or an exact name, which is a pattern of one literal segment —
// matched against the START of `segments`, the deeper segments being left to
// whatever area sits below. Returns the captured params and how many segments the
// pattern accounted for, or null when the leading segments do not fit it.
export function matchPathPrefix(
	pattern: string,
	segments: string[],
): { params: Record<string, string>; length: number } | null {
	const length = pattern.split('/').filter(Boolean).length
	if (length === 0 || segments.length < length) return null
	const params = matchPathPattern(pattern, `/${segments.slice(0, length).join('/')}`)
	return params && { params, length }
}

// The inverse of matchPathPattern — substitutes `params` into a dynamic pattern's
// `:param` segments to produce the concrete address that pattern names. Returns
// null for any param the address cannot carry, so the caller falls back rather
// than writing a URL with a literal `:eventId` segment still in it. Absent is one
// such value; the other two are a dot segment and a lone surrogate, below.
export function buildPathFromPattern(pattern: string, params: Record<string, unknown> | undefined): string | null {
	const segments = pattern.split('/').filter(Boolean)
	const built: string[] = []
	for (const segment of segments) {
		if (!segment.startsWith(':')) {
			built.push(segment)
			continue
		}
		const value = params?.[segment.slice(1)]
		if (value === undefined || value === null || value === '') return null
		// A lone surrogate throws in encodeURIComponent, and a Symbol throws in
		// String() — both reach here from a caller that never inspected the value.
		let encoded: string
		try {
			encoded = encodeURIComponent(String(value))
		} catch {
			return null
		}
		// `.` and `..` are the two ids no encoding round-trips: the URL parser
		// resolves a dot segment away before the address is ever read back, in
		// every spelling of it (`%2e%2e` included, case-insensitively), so
		// `{ eventId: '..' }` produced the pathname `/` and named a route nobody
		// asked for. Non-representable, reported the same way a missing one is.
		if (encoded === '.' || encoded === '..') return null
		built.push(encoded)
	}
	return `/${built.join('/')}`
}

export function buildQueryString(params: Record<string, string | number | boolean>): string {
	const searchParams = new URLSearchParams()

	for (const key in params) {
		if (params.hasOwnProperty(key)) {
			const value = params[key]
			if (value !== undefined && value !== null && value !== '') {
				searchParams.set(key, String(value))
			}
		}
	}

	const queryString = searchParams.toString()
	return queryString ? `?${queryString}` : ''
}
